<!--
  批量下载模态框组件
  功能：
  1. 显示批量下载进度
  2. 支持多文件同时下载
  3. 实时显示每个文件的下载进度
  4. 自动处理下载完成后的文件保存
  5. 支持取消单个文件或全部文件下载
-->
<template>
  <!-- 模态框组件，不可点击遮罩层关闭 -->
  <a-modal v-model="visible" :title="$t('downloadProgress')" width="800px" :maskClosable="false" :footer="null">
    <!--文件下载提示词-->
    <p class="download-tips">
      <a-icon type="info-circle" theme="filled" style="color: #1890ff; margin-right: 8px;" />
      {{ $t('downloadTips') }}
    </p>
    
    <!-- 操作按钮区域 -->
    <div class="action-buttons">
      <a-button 
        type="danger" 
        :disabled="!hasActiveDownloads"
        @click="cancelAllDownloads"
      >
        <a-icon type="stop" />
        {{ $t('cancelAll') }}
      </a-button>
      <a-button 
        v-if="isAllCompleted"
        type="primary" 
        @click="closeModal"
      >
        {{ $t('close') }}
      </a-button>
    </div>
    
    <!-- 进度条容器，限制最大高度，超出可滚动 -->
    <div class="progress-wrap">
      <!-- 遍历显示每个文件的下载进度 -->
      <div class="progress" v-for="item in list" :key="item.id">
        <!-- 文件信息行 -->
        <div class="file-info">
          <div class="file-name">{{ item.name }}</div>
          <div class="file-actions">
            <!-- 取消单个文件下载按钮 -->
            <a-button 
              v-if="item.progresses >= 0 && item.progresses < 100 && item.status !== 'cancelled'"
              size="small"
              type="danger"
              @click="cancelSingleDownload(item)"
            >
              <a-icon type="close" />
            </a-button>
            <!-- 下载状态图标 -->
            <a-icon 
              v-if="item.status === 'completed'" 
              type="check-circle" 
              theme="filled" 
              style="color: #52c41a; margin-left: 8px;"
            />
            <a-icon 
              v-if="item.status === 'cancelled'" 
              type="close-circle" 
              theme="filled" 
              style="color: #ff4d4f; margin-left: 8px;"
            />
            <a-icon 
              v-if="item.status === 'error'" 
              type="exclamation-circle" 
              theme="filled" 
              style="color: #ff4d4f; margin-left: 8px;"
            />
          </div>
        </div>
        <!-- 进度条组件，根据进度显示不同状态 -->
        <a-progress 
          :stroke-color="{from: '#108ee9',to: '#87d068',}" 
          :percent="item.progresses" 
          :status="getProgressStatus(item)"
        />
      </div>
    </div>
  </a-modal>
</template>

<script>
import {getDownloadUrl} from "@/request/api";
import axios from "axios";

export default {
  name: 'FileDownloadProcessModal',
  components: {},
  data() {
    return {
      visible: false, // 控制模态框显示/隐藏
      ids: [], // 存储需要下载的文件ID列表
      list: [], // 存储下载文件列表及其进度信息
      start: '',
      end: '',
      cancelTokens: {} // 存储每个文件的取消令牌
    }
  },
  computed: {
    /**
     * 检查是否有正在进行的下载
     */
    hasActiveDownloads() {
      return this.list.some(item => 
        item.progresses >= 0 && 
        item.progresses < 100 && 
        item.status !== 'cancelled' && 
        item.status !== 'error'
      )
    },
    /**
     * 检查是否所有下载都已完成（成功、取消或失败）
     */
    isAllCompleted() {
      return this.list.length > 0 && this.list.every(item => 
        item.progresses === 100 || 
        item.status === 'cancelled' || 
        item.status === 'error'
      )
    }
  },
  methods: {
    /**
     * 打开模态框并初始化下载
     * @param {Array} ids - 需要下载的文件ID数组
     * @param {String} start - 开始时间
     * @param {String} end - 结束时间
     */
    startDownload(ids, start, end){
      if(ids){
        this.ids = ids;
        this.start = start;
        this.end = end;
        this.list = []; // 重置列表
        this.cancelTokens = {}; // 重置取消令牌
        this.loadData()
        this.visible = true;
      }
    },
    /**
     * 显示模态框
     */
    show(){
      this.visible = true;
    },

    /**
     * 关闭模态框
     */
    closeModal(){
      this.visible = false;
    },

    /**
     * 加载下载数据
     * 获取下载URL并初始化下载任务
     */
    loadData(){
      getDownloadUrl({
        ids: this.ids.join(','),
        start: this.start,
        end: this.end
      }).then(res => {
        this.list = res.data;
        this.list.forEach(item => {
          // 初始化文件状态
          this.$set(item, 'status', 'downloading')
          this.downloadFile(item)
        })
      })
    },

    /**
     * 下载单个文件
     * @param {Object} file - 文件信息对象
     */
    async downloadFile(file) {
      try {
        // 初始化进度为0
        this.$set(file, 'progresses', 0)
        this.$set(file, 'status', 'downloading')

        // 创建取消令牌
        const source = axios.CancelToken.source()
        this.cancelTokens[file.id] = source

        // 获取下载URL
        const url = file.url;
        // 发起下载请求，使用blob类型接收数据
        const response = await axios({
          method: 'get',
          url: url,
          responseType: 'blob',
          cancelToken: source.token,
          onDownloadProgress: progressEvent => {
            this.updateProgress(file, progressEvent)
          }
        })

        // 处理下载完成后的文件保存
        this.handleDownload(response.data, file.name)
        // 设置进度为100%
        this.$set(file, 'progresses', 100)
        this.$set(file, 'status', 'completed')
        // 可以在这里添加成功的消息提示
        // this.$message.success(`${file.name} ${this.$t('downloadCompleted')}`)
        
        // 清理取消令牌
        delete this.cancelTokens[file.id]
              } catch (error) {
          if (axios.isCancel(error)) {
            console.log(`下载 ${file.name} 已取消`)
            this.$set(file, 'status', 'cancelled')
            // 可以在这里添加取消的消息提示
            // this.$message.info(`${file.name} ${this.$t('downloadCancelled')}`)
          } else {
            console.error(`下载 ${file.name} 失败:`, error)
            this.$set(file, 'status', 'error')
            this.$set(file, 'progresses', -1) // 设置进度为-1表示下载失败
            // 可以在这里添加失败的消息提示
            // this.$message.error(`${file.name} ${this.$t('downloadFailed')}`)
          }
          // 清理取消令牌
          delete this.cancelTokens[file.id]
        }
    },

    /**
     * 取消单个文件下载
     * @param {Object} file - 文件信息对象
     */
    cancelSingleDownload(file) {
      const cancelToken = this.cancelTokens[file.id]
      if (cancelToken) {
        cancelToken.cancel(`用户取消下载: ${file.name}`)
      }
    },

    /**
     * 取消所有下载
     */
    cancelAllDownloads() {
      Object.values(this.cancelTokens).forEach(cancelToken => {
        if (cancelToken) {
          cancelToken.cancel('用户取消所有下载')
        }
      })
      this.cancelTokens = {}
    },

    /**
     * 获取进度条状态
     * @param {Object} item - 文件信息对象
     * @returns {String} 进度条状态
     */
    getProgressStatus(item) {
      switch (item.status) {
        case 'completed':
          return 'success'
        case 'cancelled':
          return 'exception'
        case 'error':
          return 'exception'
        default:
          return item.progresses === 100 ? 'success' : 'active'
      }
    },

    /**
     * 更新文件下载进度
     * @param {Object} file - 文件信息对象
     * @param {Object} progressEvent - 进度事件对象
     */
    updateProgress(file, progressEvent) {
      if (progressEvent.total > 0) {
        // 计算下载百分比
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        this.$set(file, 'progresses', percent)
      }
    },

    /**
     * 处理文件下载
     * 创建临时下载链接并触发下载
     * @param {Blob} blobData - 文件数据
     * @param {String} filename - 文件名
     */
    handleDownload(blobData, filename) {
      // 创建Blob URL
      const url = window.URL.createObjectURL(new Blob([blobData]))
      // 创建临时下载链接
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      // 触发下载
      link.click()

      // 清理临时资源
      window.URL.revokeObjectURL(url)
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped lang="scss">
.download-tips {
  margin-bottom: 16px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.action-buttons {
  margin-bottom: 16px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.progress-wrap{
  max-height: 60vh; // 限制最大高度为视窗高度的60%
  overflow-y: auto; // 添加垂直滚动条
  padding-right: 10px; // 为滚动条预留空间
  
  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  .progress{
    padding-bottom: 20px;
    border-bottom: 1px solid #ccc;
    
    &:last-child {
      border-bottom: none; // 最后一个项目不显示底部边框
    }

    .file-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .file-name {
        flex: 1;
        font-weight: 500;
        color: #333;
      }

      .file-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }
}
</style>